/**
 * Kakao JavaScript SDK loader + share helper.
 *
 * Loads the Kakao SDK on demand (the first time `shareCampaignToKakao`
 * is called) and exposes a single share function that opens KakaoTalk's
 * native share sheet with a rich preview card.
 *
 * Two reasons not to inline this in `<SharePopover>`:
 *   1. The SDK is ~50 KB and we don't want it in every page bundle —
 *      load it only when a user opens the share sheet.
 *   2. The init flow has multiple async failure modes (script blocked,
 *      key missing, key not whitelisted on Kakao Developers). Keeping
 *      it in one helper means callers get a clean
 *      `Promise<boolean>` and can fall back to clipboard cleanly.
 *
 * Required setup on the Kakao side (one-time):
 *   - Kakao Developers > 내 애플리케이션 > Kakao Login or Kakao Share
 *     앱 > JavaScript 키를 복사 → `REACT_APP_KAKAO_JS_KEY` 환경변수에 설정.
 *   - 앱 > 플랫폼 > Web > 사이트 도메인에 `https://matchgo.ai`
 *     (and any dev domains) 등록. Without this the SDK throws
 *     `KakaoSDKException: not registered domain`.
 */

declare global {
  interface Window {
    Kakao?: {
      init?: (key: string) => void;
      isInitialized?: () => boolean;
      Share?: {
        sendDefault: (template: KakaoFeedTemplate) => void;
      };
      // Older versions used Kakao.Link instead of Kakao.Share. Both
      // expose `sendDefault`. We probe for either at call time.
      Link?: {
        sendDefault: (template: KakaoFeedTemplate) => void;
      };
    };
  }
}

interface KakaoFeedTemplate {
  objectType: 'feed';
  content: {
    title: string;
    description: string;
    imageUrl: string;
    link: {
      mobileWebUrl: string;
      webUrl: string;
    };
    imageWidth?: number;
    imageHeight?: number;
  };
  buttons?: Array<{
    title: string;
    link: {
      mobileWebUrl: string;
      webUrl: string;
    };
  }>;
}

const KAKAO_SDK_URL =
  'https://t1.kakaocdn.net/kakao_js_sdk/2.7.5/kakao.min.js';

let loadPromise: Promise<boolean> | null = null;

/**
 * Inject the Kakao SDK <script> exactly once. Returns `true` if the
 * SDK is ready and initialized, `false` otherwise (caller should fall
 * back to clipboard).
 */
function ensureKakaoSdk(): Promise<boolean> {
  if (loadPromise) return loadPromise;
  loadPromise = new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      resolve(false);
      return;
    }
    const jsKey = process.env.REACT_APP_KAKAO_JS_KEY;
    if (!jsKey) {
      // eslint-disable-next-line no-console
      console.warn(
        '[kakaoShare] REACT_APP_KAKAO_JS_KEY is not set — falling back to clipboard',
      );
      resolve(false);
      return;
    }

    // Already loaded?
    if (window.Kakao && window.Kakao.isInitialized?.()) {
      // eslint-disable-next-line no-console
      console.log('[kakaoShare] SDK already initialized');
      resolve(true);
      return;
    }

    // Already injected by another caller — wait for it to finish.
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-kakao-sdk="1"]',
    );
    if (existing) {
      existing.addEventListener('load', () => initAndResolve(jsKey, resolve), {
        once: true,
      });
      existing.addEventListener('error', (err) => {
        // eslint-disable-next-line no-console
        console.warn('[kakaoShare] reusing existing SDK script failed', err);
        resolve(false);
      }, { once: true });
      return;
    }

    // Inject the script. We deliberately omit Subresource Integrity (SRI):
    // Kakao ships a versioned URL, and a hardcoded SRI hash here would
    // (a) fail silently every time Kakao bumps the version of 2.7.x,
    // (b) require manual updates that nobody remembers to do.
    // The CDN is HTTPS-only and hosted on Kakao infrastructure, which
    // is the same trust boundary as the share endpoint we're calling.
    const script = document.createElement('script');
    script.src = KAKAO_SDK_URL;
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.defer = true;
    script.dataset.kakaoSdk = '1';
    script.addEventListener(
      'load',
      () => initAndResolve(jsKey, resolve),
      { once: true },
    );
    script.addEventListener(
      'error',
      (err) => {
        // eslint-disable-next-line no-console
        console.warn(
          '[kakaoShare] SDK script failed to load (network / blocked / CSP)',
          err,
        );
        resolve(false);
      },
      { once: true },
    );
    document.head.appendChild(script);
    // eslint-disable-next-line no-console
    console.log('[kakaoShare] injected SDK script', KAKAO_SDK_URL);
  });
  return loadPromise;
}

function initAndResolve(jsKey: string, resolve: (v: boolean) => void): void {
  try {
    if (!window.Kakao) {
      resolve(false);
      return;
    }
    if (!window.Kakao.isInitialized?.()) {
      window.Kakao.init?.(jsKey);
    }
    resolve(window.Kakao.isInitialized?.() ?? false);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[kakaoShare] Kakao.init failed', err);
    resolve(false);
  }
}

export interface KakaoCampaignShareInput {
  /** Page URL (campaign canonical, e.g. https://matchgo.ai/campaigns/abc). */
  url: string;
  /** Card title — user-facing campaign name + brand. */
  title: string;
  /** Card description — short subhead under the title. */
  description: string;
  /** 1200×630 https URL on a CloudFront / matchgo.ai allowlisted host. */
  imageUrl: string;
  /** Label for the primary button on the Kakao card. */
  buttonLabel?: string;
}

/**
 * Open KakaoTalk's native share sheet for a campaign. Returns:
 *   - `true`  → SDK opened the share UI (user may still cancel)
 *   - `false` → SDK unavailable; caller should fall back to clipboard
 *
 * Failure modes that return `false`:
 *   - `REACT_APP_KAKAO_JS_KEY` not set
 *   - Browser blocked the SDK script (rare; ad-blockers usually allow it)
 *   - Kakao Developers domain whitelist mismatch (SDK throws inside `init`)
 *   - Browser blocked the popup (Kakao opens a popup on desktop)
 *
 * Successful case opens the share sheet *or* the share popup, depending
 * on platform — no extra UI needed from the caller.
 */
export async function shareCampaignToKakao(
  input: KakaoCampaignShareInput,
): Promise<boolean> {
  const ready = await ensureKakaoSdk();
  if (!ready || typeof window === 'undefined' || !window.Kakao) {
    return false;
  }
  // SDK 2.x exposes Kakao.Share; older bundles still ship Kakao.Link.
  const api = window.Kakao.Share || window.Kakao.Link;
  if (!api) {
    // eslint-disable-next-line no-console
    console.warn('[kakaoShare] neither Kakao.Share nor Kakao.Link available');
    return false;
  }

  try {
    api.sendDefault({
      objectType: 'feed',
      content: {
        title: input.title,
        description: input.description,
        imageUrl: input.imageUrl,
        imageWidth: 1200,
        imageHeight: 630,
        link: {
          mobileWebUrl: input.url,
          webUrl: input.url,
        },
      },
      buttons: [
        {
          title: input.buttonLabel || '캠페인 보러가기',
          link: {
            mobileWebUrl: input.url,
            webUrl: input.url,
          },
        },
      ],
    });
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[kakaoShare] sendDefault failed', err);
    return false;
  }
}
